import {
  Alert,
  AlertTitle,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  ListItem,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import InfoAlert from "../common/InfoAlert";
import { AppEntryDocRepository } from "../repositories/AppEntryDocRepository";
import React, { useEffect, useRef, useState } from "react";
import { CiFileOn } from "react-icons/ci";
import { Enumerations } from "../../config/Enumerations";
import { TfiMoreAlt } from "react-icons/tfi";
import { GoEye, GoUpload } from "react-icons/go";
import { FiDownload } from "react-icons/fi";
import PDFDialogViewer from "../entries/PDFDialogViewer";
import { AuthRepository } from "../repositories/AuthRepository";
import { AppReviewRepository } from "../repositories/AppReviewRepository";
import { useDispatch } from "react-redux";
import {
  notifyShowErrorMessage,
  notifyShowSuccessMessage,
} from "../../store/AlertSlice";
import { ErrorMessageResolver } from "../common/ErrorMessageResolver";
import ConfirmationDialog from "../common/ConfirmationDialog";
import { ArrowBack, Check } from "@mui/icons-material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { GenerateReviewDecision } from "./GenerateReviewDecision";
import { ReviewDecisionSigning } from "./ReviewDecisionSigning";

const options = [
  { key: "preview", label: "Преглед", icon: <GoEye size={18} /> },
  { key: "download", label: "Преземи", icon: <FiDownload size={18} /> },
];

export default function ReviewDecisions({
  appReview,
  appReviewDocs,
  setAppReview,
}) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const fileInput = useRef();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [menuOptions, setMenuOptions] = useState([]);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [openPdfDialog, setOpenPdfDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [generatedDecision, setGeneratedDecision] = useState();
  const [saving, setSaving] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState();
  const [openDecisionSigning, setOpenDecisionSigning] = useState();
  const [error, setError] = useState(null);

  const handleReviewDecision = (decisionFlag) => {
    setAppReview((prev) => ({ ...prev, isApproved: decisionFlag }));
  };

  const handleMenuClick = (event, fileName) => {
    setMenuAnchor(event.currentTarget);
    setSelectedFile(fileName);

    const filtered = options.filter((option) => {
      if (fileName.includes("-signed") && option.key === "sign") {
        return false;
      }
      return true;
    });
    setMenuOptions(filtered);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedFile(null);
  };

  const getByFileName = (fileName) => {
    AppEntryDocRepository.getDocument(appReview?.appEntry?.id, fileName, {
      responseType: "blob",
    })
      .then((res) => {
        setPdfBlob(res.data);
        setOpenPdfDialog(true);
      })
      .catch((err) => {
        console.log(err);
        dispatch(notifyShowErrorMessage(ErrorMessageResolver.resolve(err)));
        setError(ErrorMessageResolver.resolve(err));
      });
  };

  const downloadByFileName = (fileName) => {
    AppEntryDocRepository.getDocument(appReview?.appEntry?.id, fileName, {
      responseType: "blob",
    })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const isMvrRole = AuthRepository.hasAnyRole("ROLE_MVR_REVIEWER");
  const isJpdpRole = AuthRepository.hasAnyRole("ROLE_JPDP_REVIEWER");
  const isMvrRoleRecevier = AuthRepository.hasAnyRole("ROLE_MVR_RECEIVER");
  const isJpdpRoleReceiver = AuthRepository.hasAnyRole("ROLE_JPDP_RECEIVER");
  const isMvrSigner = AuthRepository.hasAnyRole("ROLE_MVR_SIGNER");
  const isJpdpSigner = AuthRepository.hasAnyRole("ROLE_JPDP_SIGNER");

  const visibleDocs = React.useMemo(() => {
    return (
      appReview?.appEntry?.docs?.filter((doc) => {
        if (isMvrRole || isMvrRoleRecevier || isMvrSigner)
          return doc.documentType === "DECISION_MVR_SIGNED";
        if (isJpdpRole || isJpdpRoleReceiver || isJpdpSigner)
          return doc.documentType === "DECISION_JPDP_SIGNED";
        return false;
      }) ?? []
    );
  }, [appReview, isMvrRole, isJpdpRole, isMvrSigner, isJpdpSigner]);

  const visibleDocsForSignature = React.useMemo(() => {
    const docs = appReview?.appEntry?.docs
      ?.filter((doc) => {
        if (isMvrRole || isMvrSigner)
          return doc.documentType === "DECISION_MVR";
        if (isJpdpRole || isJpdpSigner)
          return doc.documentType === "DECISION_JPDP";
        return false;
      })
      ?.sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));
    return docs && docs.length > 0 ? [docs[0]] : null;
  }, [appReview, isMvrRole, isJpdpRole]);

  const showButtons = React.useMemo(() => {
    const docs = appReview?.appEntry?.docs ?? [];
    const types = new Set(docs.map((d) => d.documentType));
    const hasMvr = types.has("DECISION_MVR");
    const hasJpdp = types.has("DECISION_JPDP");
    const type = appReview?.appEntry?.type;
    const forCorrection =
      appReview?.correctionComment &&
      (appReview?.reviewStatus == "PROCESSING" ||
        appReview?.reviewStatus == "PENDING");
    const isPermanent = type === "PERMANENT_TRAFFIC_CHANGE";
    const isTemporary = type === "TEMPORARY_TRAFFIC_CHANGE";

    if (forCorrection) return true;

    if (isPermanent) {
      if (isMvrRole) return !hasMvr;
      if (isJpdpRole) return !hasJpdp;
    } else if (isTemporary) {
      if (isMvrRole) return !hasMvr;
      return false;
    }
    return false;
  }, [appReview, isMvrRole, isJpdpRole]);

  return (
    <React.Fragment>
      <Grid
        item
        xs={12}
        className="other-documents-container"
        sx={{ mt: 2 }}
        style={{ padding: "10px" }}
      >
        <div
          className="other-documents-title-container"
          style={{ marginTop: "10px" }}
        >
          <div className="other-documents-title">
            <Typography variant="h6" className="title-text">
              Одлуки
            </Typography>
          </div>
        </div>

        {showButtons && (
          <>
            {appReview?.isApproved == null ? (
              <Grid container spacing={2} sx={{ p: 1 }}>
                {(appReview?.reviewStatus == "PROCESSING" ||
                  appReview?.reviewStatus == "PENDING") &&
                  appReview?.correctionComment && (
                    <Grid item xs={12}>
                      <Alert variant="filled" severity="error">
                        <AlertTitle>
                          Предметот е вратен на корекција со коментар:{" "}
                          {appReview?.correctionComment}
                        </AlertTitle>
                      </Alert>
                    </Grid>
                  )}
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    align="center"
                    fontWeight={"bold"}
                  >
                    Изберете тип на одлука
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="error"
                    size="large"
                    onClick={() => handleReviewDecision(false)}
                  >
                    {!loading ? (
                      <>Негативна одлука</>
                    ) : (
                      <CircularProgress size={20} id="circular-progress" />
                    )}
                  </Button>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    size="large"
                    onClick={() => handleReviewDecision(true)}
                  >
                    {!loading ? (
                      <>Позитивна одлука</>
                    ) : (
                      <CircularProgress size={20} id="circular-progress" />
                    )}
                  </Button>
                </Grid>
              </Grid>
            ) : (
              <React.Fragment>
                <Grid
                  item
                  xs={12}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  sx={{ mt: 1, mb: 1 }}
                >
                  {!generatedDecision && (
                    <GenerateReviewDecision
                      open={!generatedDecision}
                      setOpen={setGeneratedDecision}
                      appReview={appReview}
                      setAppReview={setAppReview}
                    />
                  )}
                </Grid>
              </React.Fragment>
            )}
          </>
        )}

        <Grid item xs={12}>
          {visibleDocs.length > 0 && (
            <Box className="other-documents-box">
              {visibleDocs.map((doc, index) => (
                <ListItem key={index} className="document-item dark-border">
                  <div className="document-icon">
                    <CiFileOn color="#2d2d2d" size={25} />
                  </div>
                  <div className="document-details">
                    <Typography
                      variant="inherit"
                      className="document-name dark"
                    >
                      {console.log(doc)}
                      {Enumerations.DocumentType[doc?.documentType] ||
                        doc?.documentType}
                    </Typography>
                  </div>
                  <div className="document-actions">
                    <IconButton
                      disableRipple
                      aria-label="more"
                      id="long-button"
                      aria-controls={open ? "long-menu" : undefined}
                      aria-expanded={open ? "true" : undefined}
                      aria-haspopup="true"
                      onClick={(e) => handleMenuClick(e, doc.fileName)}
                    >
                      <TfiMoreAlt
                        size={20}
                        color={theme.palette.secondary.main}
                      />
                    </IconButton>
                    <Menu
                      id="long-menu"
                      anchorEl={menuAnchor}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                      open={Boolean(menuAnchor)}
                      onClose={handleMenuClose}
                      slotProps={{
                        paper: {
                          style: {
                            width: "20ch",
                            boxShadow:
                              "rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px",
                          },
                        },
                        list: {
                          "aria-labelledby": "long-button",
                        },
                      }}
                    >
                      {menuOptions.map((option) => (
                        <MenuItem
                          key={option.label}
                          disableRipple
                          onClick={() => {
                            if (option.key === "preview") {
                              getByFileName(selectedFile);
                            } else if (option.key === "download") {
                              downloadByFileName(selectedFile);
                            }
                            handleMenuClose();
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            gap: "10px",
                            margin: "1px 5px",
                            borderRadius: "4px",
                            "&:hover": {
                              backgroundColor: "#eaf5ff",
                            },
                          }}
                        >
                          {option.icon}
                          <Typography
                            variant="subtitle2"
                            style={{ fontSize: "14px" }}
                          >
                            {option.label}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Menu>
                  </div>
                </ListItem>
              ))}
            </Box>
          )}
        </Grid>
        <Grid item xs={12}>
          {(!visibleDocs || visibleDocs?.length == 0) &&
            visibleDocsForSignature && (
              <Box className="other-documents-box">
                {visibleDocsForSignature &&
                  visibleDocsForSignature?.map((doc, index) => (
                    <ListItem key={index} className="document-item dark-border">
                      <div className="document-icon">
                        <CiFileOn color="#2d2d2d" size={25} />
                      </div>
                      <div className="document-details">
                        <Typography
                          variant="inherit"
                          className="document-name dark"
                        >
                          {Enumerations.DocumentType[doc?.documentType] ||
                            doc?.documentType}
                        </Typography>
                      </div>
                      <div className="document-actions">
                        <IconButton
                          disableRipple
                          aria-label="more"
                          id="long-button"
                          aria-controls={open ? "long-menu" : undefined}
                          aria-expanded={open ? "true" : undefined}
                          aria-haspopup="true"
                          onClick={(e) => handleMenuClick(e, doc.fileName)}
                        >
                          <TfiMoreAlt
                            size={20}
                            color={theme.palette.secondary.main}
                          />
                        </IconButton>
                        <Menu
                          id="long-menu"
                          anchorEl={menuAnchor}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                          }}
                          open={Boolean(menuAnchor)}
                          onClose={handleMenuClose}
                          slotProps={{
                            paper: {
                              style: {
                                width: "20ch",
                                boxShadow:
                                  "rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px",
                              },
                            },
                            list: {
                              "aria-labelledby": "long-button",
                            },
                          }}
                        >
                          {menuOptions.map((option) => (
                            <MenuItem
                              key={option.label}
                              disableRipple
                              onClick={() => {
                                if (option.key === "preview") {
                                  getByFileName(selectedFile);
                                } else if (option.key === "download") {
                                  downloadByFileName(selectedFile);
                                }
                                handleMenuClose();
                              }}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                gap: "10px",
                                margin: "1px 5px",
                                borderRadius: "4px",
                                "&:hover": {
                                  backgroundColor: "#eaf5ff",
                                },
                              }}
                            >
                              {option.icon}
                              <Typography
                                variant="subtitle2"
                                style={{ fontSize: "14px" }}
                              >
                                {option.label}
                              </Typography>
                            </MenuItem>
                          ))}
                        </Menu>
                      </div>
                    </ListItem>
                  ))}
              </Box>
            )}
        </Grid>

        <Grid item xs={12} sx={{ textAlign: "center" }}>
          {appReview &&
            appReview?.reviewStatus === "WAITING_FOR_SIGNATURE" &&
            AuthRepository.hasAnyRole([
              "ROLE_MVR_SIGNER",
              "ROLE_JPDP_SIGNER",
            ]) && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setOpenDecisionSigning(true);
                }}
              >
                Прегледај и потпиши одлука
              </Button>
            )}
        </Grid>
      </Grid>
      <PDFDialogViewer
        openPdfDialog={openPdfDialog}
        setOpenPdfDialog={setOpenPdfDialog}
        pdfBlob={pdfBlob}
      />
      {saving && (
        <Backdrop sx={{ color: "#fff", zIndex: 9999 }} open={saving}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      {openConfirmationDialog && (
        <ConfirmationDialog
          open={Boolean(openConfirmationDialog)}
          title={openConfirmationDialog.title}
          message={openConfirmationDialog.message}
          onCancel={() => setOpenConfirmationDialog()}
          onConfirm={openConfirmationDialog.onConfirm}
        />
      )}
      {appReview && appReview?.reviewStatus === "WAITING_FOR_SIGNATURE" && (
        <ReviewDecisionSigning
          open={openDecisionSigning}
          setOpen={setOpenDecisionSigning}
          appReview={appReview}
          setAppReview={setAppReview}
        />
      )}
    </React.Fragment>
  );
}
